import ReactDOM from "react-dom";
import { rootModuleId, RootRoute } from "@app/const";
import { defaultServiceContext } from "@app/contexts";
import { LoadingSpinner } from "@app/form_components";
import {
  DesignClass,
  ObjectClass,
  UserFeatureFlag
} from "@app/types";
import { createUuid, renderByRoute } from "@app/utilities";
import Utility from "@app/utilities/Utility";
import CommonModuleInstallOptions from "../CommonModuleInstallOptions";
import BaseDialog from "./BaseDialog";
import BlankElementEmbed from "./BlankElementEmbed";
import ElementGroup from './ElementGroup';
import ImportElementEmbed from "./ImportElementEmbed";
import ImportModule from "./ImportModule";

const {
    avsService,
    featureFlagService
} = defaultServiceContext;

export default class NewElementDialog extends BaseDialog {
    /**
     * Dialog to add a new element to application, with search ability
     * @module NewElementDialog
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);
        this.state = {
            query: "",
            isSet: false,
            modulesLoaded: false,
            loadingCommonModule: false,
            downloadedCommonModule: null,
            displayInstallOptions: null,
            installOptionsConfig: null
        };
        this.itemWidth = 0;
        this.dynamicWidth = 45;
        this.toggleHandle = this.handleToggle.bind(this);
        this.addHandle = this.handleAdd.bind(this);
        this.installOptionsChangeConfigHandle = this.handleOnInstallOptionsChangeConfig.bind(this);
        this.selected = {};
        this.coOrdinates = this.props.event;
        this.rootModuelId = null;
    }

    componentDidMount() {
        super.componentDidMount();
        // listeners
        document.addEventListener("ev-dialog-toggle", this.toggleHandle);
        document.addEventListener("ev-dialog-add", this.addHandle);
        // set up the UI
        
        this.getDomSelf().find(".new_" + this.props.type).tab('show');
        this.getDomSelf().find(".nav-button a").on("click", () => {
            this.setSelected("");
            this.setState({displayInstallOptions: false});
        });
        this.itemWidth = parseInt(this.getDomSelf().find(".element-item").width()) / 100 * this.getDomSelf().outerWidth();
        this.getDomSelf().find('#dialog_q').focus();
        this.getCommonModulesForDialog();
         
    
    }

    componentWillUnmount(){
        document.removeEventListener("ev-dialog-toggle", this.toggleHandle);
        document.removeEventListener("ev-dialog-add", this.addHandle);
    }

    
    async getCommonModulesForDialog() {
        let commonModules = await avsService.getCommonModuleDesigns();
        const isDevEnv = window.location.hostname === 'localhost';
        if (!isDevEnv) {
            commonModules = commonModules.filter((design) => {
              return !design.name.includes('Test');
            });
        }

        window.apm.knowledgeBase.setCommonModuleElements(commonModules);
        this.setState({modulesLoaded: true});
    }

    unregister() {
        document.removeEventListener("ev-dialog-toggle", this.toggleHandle);
        document.removeEventListener("ev-dialog-add", this.addHandle);
    }

    /**
     * Update and filter dialog
     * @param {string} query
     */
    doFilter(query) {
        this.setState({ query: query });
    }
    /**
     * Set currently selected template item
     * @param {Object} item
     * @param {string} type
     */
    setSelected(item, type) {
        if (item) {
            this.selected = {
                item: item,
                type: type
            }
        } else {
            $(".element-item").removeClass("selected");
        }
    }

    /**
     * Render element group
     * @param {string} type
     * @returns {Array<XML>}
     */
    renderElementItems(type) {
        // loading common module
        if(this.state.loadingCommonModule && type === 'module'){
            return (
                <div style={{minHeight: '87%', width: '95%', display:'flex', alignItems: 'center', position: 'absolute'}}>
                    <LoadingSpinner message={"Importing Common Module..."}/>
                </div>
            )
        }
        
        // common module has install options 
        if(this.state.displayInstallOptions && type === 'module'){
        
            return <CommonModuleInstallOptions 
                        commonModule={this.state.downloadedCommonModule} 
                        type="import"
                        handleOnChangeConfig={this.installOptionsChangeConfigHandle} />
            
        }
        // default display
        else {
            const items = [];
        
            // new 'blank' element
            if(type === 'module' || type === 'variable'){
                items.push({
                    id: "__blank",
                    name: window.l.popup.blank,
                    type: type,
                    props: {
                        iconClass: "file-plus",
                        config: {
                            description: window.l.popup.create_new_space + window.l.popup[type] + window.l.popup.space_from_scratch
                        }
                    }
                });
            }
            if(type === 'module') {
                // import
                items.push({
                    id: "__import",
                    name: window.l.popup.import,
                    type: type,
                    props: {
                        iconClass: "upload-cloud",
                        config: {
                            description: window.l.popup.import_space + window.l.popup[type] + window.l.popup.space_from_json_file
                        }
                    }
                });
            }
            var elements = [
                <ElementGroup category={"__default"} key={"__default"} context={this} type={type}
                    isAsync={false}
                    items={items}
                    dynamicWidth={this.dynamicWidth}
                />
            ];

            return this.getCommonElementsByType(type, elements);
        }
        
        
        
    }

    getCommonElementItems(el){
        const elementItems = el.items;
        
        if(elementItems.length === 0){
            return;
        }

        // TODO: skip this
        const isCommonModule = Utility.getIsCommonModule(elementItems[0]);
        
        if(this.state.query) {
            try{
                const regex = new RegExp(this.state.query, "i");
                return (isCommonModule) ? elementItems.filter(el => el.application.match(regex)) 
                    : elementItems.filter(el => el.name.match(regex));
            }
            catch(err){
                console.log(err.message);
            }
        }
            
        return elementItems;
    }
    
    getCommonElementsByType(type, elements){
        if(!type) { 
            return;
        }
        const commonElements = window.apm.knowledgeBase.getCommonElements(type + "s");
        
        const isAsync = (type === 'module') ? true : false;

        commonElements.forEach(el => {
            elements.push(
                <ElementGroup 
                    category={el.category} 
                    key={el.category} 
                    context={this} 
                    type={type}
                    isAsync={isAsync}
                    asyncReady={this.state.modulesLoaded}
                    items={this.getCommonElementItems(el)}
                    dynamicWidth={this.dynamicWidth} />
            );
        })
        
        return elements;
    }
    /**
     * Update item display width; tighter columns for bigger dialog to show more stuff in a single row
     * @param event
     */
    handleToggle() {
        if (this.itemWidth) {
            this.dynamicWidth = this.itemWidth / this.getDomSelf().outerWidth() * 100;
            this.getDomSelf().find(".element-item").css("width", this.dynamicWidth + "%");
        }
    }
    /**
     * Handle changes on InstallOptions component
     */
    handleOnInstallOptionsChangeConfig(key, value){
        this.setState({downloadedCommonModule: {...this.state.downloadedCommonModule, sensitiveOverrides: {...this.state.downloadedCommonModule?.sensitiveOverrides, enabled: !value}}});
        this.setState({installOptionsConfig: {[key]: value}});
    }
    /**
     * Handle add to application event
     */
    async handleAdd() {
        if(!this.selected.item || !this.selected.type){
            window.apm.popupMessage(window.l.popup.select_one_before_add);
            return;
        }

        const item = this.selected.item;
        const treeDom = $('#tl_container');
        //Feature flag 1577
        const wizardConfigFeatureFlagEnabled = featureFlagService.isFeatureOn(UserFeatureFlag.wizardConfig);
        // default items
        if (this.selected.item.id === "__import") {
            w2popup.message({
                width: 500,
                height: 400,
                html: '<div class="import_container"></div>',
                onOpen: () => {
                    this.intervalHandler = setInterval(() => {
                        if($(".import_container")){
                            clearInterval(this.intervalHandler);

                            ReactDOM.render(
                                <ImportElementEmbed key={createUuid()} addFunc={NewElementDialog.addElement}/>, $(".import_container").get(0));
                        }
                    }, 10);
                    
                }
            });
            return;
        }

        else if (this.selected.item.id === "__blank") {
            w2popup.message({
                width: 500,
                height: 600,
                html: '<div class="blank_container"></div>',
                onOpen: () => {
                    this.intervalHandler = setInterval(() => {
                        if($(".blank_container")){
                            clearInterval(this.intervalHandler);

                            $(".w2ui-message").css("transform", "");
                            
                            ReactDOM.render(
                                <BlankElementEmbed key={createUuid()} addFunc={NewElementDialog.addElement} type={this.selected.type} coOrdinates={this.coOrdinates}/>, $(".blank_container").get(0));
                        }
                    }, 10);
                }
            });
            return;
        }
        
        else if(this.selected.type === "module") {
            const { id: commonDesignId, name } = item;

            this.setState({loadingCommonModule: true});
            const previouslyInstalled = window.apm.serialized.components.modules.some((mod) => {
                return mod.name.toLowerCase() === name.toLowerCase()
            });
            const previouslyInstalledMessage = `<b>Common Module ${name}</b> is already installed. Please change the name of installed Common Module before importing another version.`;
            const installSelfMessage = `<b>Common Module ${name}</b> cannot be installed into itself.`;

            // authoring...
            if(window.apm.serialized.designId === commonDesignId) {
                window.apm.popupMessage(installSelfMessage);
                this.setState({loadingCommonModule: false});
            }
            // standard design import
            else if(previouslyInstalled){
                window.apm.popupMessage(previouslyInstalledMessage);
                this.setState({loadingCommonModule: false});
            } 
            // hit continue to install common module with sensitive overrides 
            else if(this.state.displayInstallOptions) {
                let commonModule = this.state.downloadedCommonModule;
                const redacted = this.state.installOptionsConfig?.redactSensitive || false;
                
                await window.apm.commonModuleManager.importCommonModule(commonModule, null, redacted);
                document.dispatchEvent(new CustomEvent('ev-dialog-close'));
                w2popup.close();
            }
            
            // default
            else {
                const selectedTempCopy = JSON.parse(JSON.stringify(this.selected));
                const commonBranches = await avsService.getBranches(commonDesignId);
                // Main is always the first branch.
                const commonBranchId = commonBranches[0].id;
                const latestVersion = (
                    await avsService.getLatestVersion(commonBranchId)
                ).version;
                const commonModule = await avsService.getDesign(commonBranchId, latestVersion);
                const findCMRecursion = this.commonModuleRecursion(window.apm.serialized, commonModule);
                const recursionMessage = `<b>Common Module ${name}</b> cannot be installed as it contains a reference to the current Common Module.`;

                if(findCMRecursion) {
                    window.apm.popupMessage(recursionMessage);
                    this.setState({loadingCommonModule: false});

                } else {
                    if (commonModule && wizardConfigFeatureFlagEnabled && !commonModule.wizardConfig) {
                        window.apm.popupMessage(`<b>Common Module ${name}</b> does not support wizard configuration at this time`,commonModule);
                    }
                    else if (commonModule&&wizardConfigFeatureFlagEnabled&&commonModule.wizardConfig) {
                        window.apm.openCommonModuleWizard(commonModule, true);
                    }
                    else{
                        // sensitivity override
                        if(commonModule.sensitiveOverrides && commonModule.sensitiveOverrides.enabled){
                            // to be passed to CommonModuleManager
                            this.setState({downloadedCommonModule: commonModule});

                            // set state for display install options
                            this.setState({loadingCommonModule: false});

                            this.setState({displayInstallOptions: true});

                            this.selected = selectedTempCopy;
                            return;

                        } else {
                            await window.apm.commonModuleManager.importCommonModule(commonModule);
                            document.dispatchEvent(new CustomEvent('ev-dialog-close'));
                            w2popup.close();
                        }
                    }
                }
            }
        }
            
        else {
            item.id = createUuid();
            const newNode = NewElementDialog.addElement(treeDom, item,true, null,this.coOrdinates);
            window.apm.refreshApplicationTree();
            w2popup.close();
            if(newNode) {
                window.apm.postEventToCurrentView({
                    target: "request-component-panel",
                    event: newNode,
                    shouldFocusInput: true,
                });
            }
        }

        this.selected = {};
    }

    commonModuleRecursion(serialized, commonModule) {

        const components = commonModule.components;
        let isRecursive = false;

        components.modules.forEach(cm => {            
            if(cm.name.toLowerCase() === serialized.application.toLowerCase()){
                isRecursive = true;
            }
        });

        return isRecursive;
    }

    /**
     * Update the Work Flow Tool name for each node
     * @param {Array} nodes 
     */
    static updateWFTNames(nodes){
        nodes.filter(node => node._class === ObjectClass.prompt).forEach(node => {
            node.wftName = window.apm.serialized.application + "_" + node.wftName; 
        });    
    }

    /**
     * Add to application model
     * @param {jQuery} treeDom Treeview reference the element will be added to
     * @param {Object} app
     */
    // TODO: Legacy code to support ImportCVS functionality
    // TODO: This is broken
    static addCommonModule(treeDom, app) {
        var rootModule;
        if (app.graphQLSchemas) {
            if (window.apm.serialized.graphQLSchemas) {
                for (var gh = 0; gh < app.graphQLSchemas.length; gh++) {
                    var ghIndex = window.apm.serialized.graphQLSchemas.findIndex(ele => ele.id === app.graphQLSchemas[gh].id);

                    if (ghIndex === -1) { app.graphQLSchemas[gh].moduleId = app._id; app.graphQLSchemas[gh].isCommonModule = true; window.apm.serialized.graphQLSchemas.push(app.graphQLSchemas[gh]); }
                }
            }
            else {
                window.apm.serialized.graphQLSchemas = app.graphQLSchemas;
            }
        }
        if (app.csvData) {
            if (window.apm.serialized.csvData) {
                for (var gh = 0; gh < app.csvData.length; gh++) {
                    var ghIndex = window.apm.serialized.csvData.findIndex(ele => ele.id === app.csvData[gh].id);

                    if (ghIndex === -1) { app.csvData[gh].moduleId = app._id; app.csvData[gh].isCommonModule = true; window.apm.serialized.csvData.push(app.csvData[gh]); }
                }
            }
            else {
                window.apm.serialized.csvData = app.csvData;
            }
        }

        for (var i = 0; i < app.components.modules.length; i++) {
            var comModule = { client: app.client, application: app.application, version: app.repoVersion };
            var item = app.components.modules[i];
            item.props.iconClass = "globe";
            item.props.readOnly = true;
            item.props.isCommonModule = true;
            if (item.id === rootModuleId) {
                item.name = app.application.toUpperCase().replace(/ /g, "_");
                item.props.isRootModule = true;
                item.props.config.parameters = app.parameters;
                item.isHidden = false;
                item.props.createdTime = window.apm.timestamp;
                item.props.lastLocalChange = window.apm.timestamp;
                item.props.config.version = app.repoVersion;

                for (var pc = 0; pc < item.versions[1].flow.nodes.length; pc++) {
                    if (item.versions[1].flow.nodes[pc]._class === ObjectClass.prompt) {
                        item.versions[1].flow.nodes[pc].wftName = window.apm.serialized.application + "_" + item.versions[1].flow.nodes[pc].wftName;
                    }
                }

                rootModule = _.cloneDeep(item);
                rootModule.id = createUuid();
                
            }
            else {
                item.isHidden = true;
                var existingModules = _.filter(window.apm.serialized.components.modules, { name: item.name });
                for (var pc = 0; pc < item.versions[1].flow.nodes.length; pc++) {
                    if (item.versions[1].flow.nodes[pc]._class === ObjectClass.prompt) {
                        item.versions[1].flow.nodes[pc].wftName = window.apm.serialized.application + "_" + item.versions[1].flow.nodes[pc].wftName;
                    }
                }
                if (existingModules.length > 0 && existingModules[0].props.isCommonModule) {
                    var noId = item.id;
                    if (JSON.stringify(app.components.modules).includes(noId)) {
                        item.id = createUuid();
                        app.components.modules = JSON.stringify(app.components.modules).replace(new RegExp(noId, 'gi'), item.id);
                        app.components.modules = JSON.parse(app.components.modules);
                    }
                    if (JSON.stringify(rootModule).includes(noId)) {
                        item.id = createUuid();
                        rootModule = JSON.stringify(rootModule).replace(new RegExp(noId, 'gi'), item.id);
                        rootModule = JSON.parse(rootModule);
                    }
                    this.addElement(treeDom, item, true, noId);
                }
                else {
                    this.addElement(treeDom, item, true);
                }
            }
        }
        this.addElement(treeDom, rootModule);
    }


    /**
     * Add to application model either download or import
     * @param {jQuery} treeDom Treeview reference the element will be added to
     * @param {Object} item
     * @param {boolean} useSameId use the same ID as the original if set to true
     */
    static addElement(treeDom, item, useSameId = false, itemId = null, coOrdinates=null) {
        const addNode = (treeDom, treeContainerParent, node, renderView) => {
            
            if(!node.isHidden){
                treeDom.jstree().create_node(treeContainerParent, Utility.getTreeNode(node, item.isHidden), "last", function (new_node) {
                    const oldName = new_node.text;

                    $('#tl_container').jstree().edit(new_node, null, () => {
                        if (new_node.type !== "variable") {
                            new_node.text = new_node.text.toUpperCase().replace(/ /g, "_");
                        }
                        if (window.apm.isTreeNameDuplicated(treeDom, new_node.text, new_node.type, treeContainerParent, new_node.id)) {
                            window.apm.renameNode(new_node.id, oldName, treeDom);
                        }
                        window.apm.renameNode(new_node.id, new_node.text, treeDom);

                        treeDom.jstree().deselect_all();
                        treeDom.jstree().select_node(new_node);

                    });
                });
            }
            
            if (useSameId) node.id = item.id;
            

            // CM TODO: 
            if (
                node._class === ObjectClass.prompt
                && Utility.getIsCommonModule(window.apm.serialized)
            ) {
                node.wftName = window.apm.serialized.application + "_" + node.name;
            }

            if (
                node.props.isCommonModule
                && node._class === DesignClass.module
            ) {
                node.props.config.inputs = window.apm.getModuleInputs(node);
            }

            window.apm.addNode(node, renderView);
            
            if (coOrdinates && !node.isHidden && !coOrdinates.insert && !coOrdinates.replace) {
                window.apm.postEventToCurrentView({ target: "request-create-node-at-event", event: coOrdinates, node: node });
            } else if(coOrdinates && !node.isHidden && coOrdinates.insert) {
                window.apm.postEventToCurrentView({ target: "request-insert", event: coOrdinates, node: node });
            } else if(coOrdinates && !node.isHidden && coOrdinates.replace) {
                window.apm.postEventToCurrentView({ target: "request-replace", event: coOrdinates, node: node });
            }
        }; // end addNode

        // CM TODO: is this needed for CMs hmm?
        const resolveDependencies = (exports) => {
            ImportModule(exports);
            w2popup.close();
        };
            
        // save variable to a folder in jstree based on the variable scope
        const getTreeContainerParent = (node)=>{
            const simultaneousEnabled = window.apm.serialized.omniChannel.isOmniChannel;
            
            if(node.type === "object"){
                return node.props.nodeTypeName;
            }
            else if(simultaneousEnabled && node.type === "variable"){
                return node.props.config.scope;
            }
            else{
                node.type + "s"
            }
        }

        const isImport = item.node;

        if(!isImport){ // D2 nodes, including common modules.
            const newNode = _.cloneDeep(item);
            const treeContainerParent = getTreeContainerParent(newNode);
            const treeNodeExists = treeDom.jstree().get_node(treeContainerParent);
            const isCommonModuleNode = newNode.props.moduleId || false;

            // copied nodes require new id
            // do not change common module nodes here. See CommonModuleManager.importCommonModuleComponents 
            if(!isCommonModuleNode){
                newNode.id = createUuid();
            }
        
            newNode.props.createdTime = window.apm.timestamp;

            // CM TODO: not being added to flow
            newNode.props.timeAddedToFlow = window.apm.timestamp;

            if(!isCommonModuleNode){
                newNode.name = window.apm.getEligibleTreeName(treeDom, item.name, item.type, item.props.nodeTypeName);
            }
            
            if (newNode.type === "object" && !treeNodeExists) {
                    // add type name folder if not exist
                    treeDom.jstree().create_node("objects", 
                        {
                            "id": newNode.props.nodeTypeName,
                            "text": newNode.props.nodeTypeName,
                            "type": "objects",
                            "state": { "opened": true },
                            "children": []
                        }, 
                        "last", 
                        () => {
                            addNode(treeDom, treeContainerParent, newNode, !isCommonModuleNode);
                        }
                    );
                
            }
            // default
            else{
                addNode(treeDom, treeContainerParent, newNode, !isCommonModuleNode);
            }
            if(!isCommonModuleNode){
                // update settings props
                renderByRoute(RootRoute.settings);
                renderByRoute(RootRoute.intents);
            }

            return newNode;
        } 
        else { // isImport

            const isModuleImport = item.node.type === "module";

            // If import is a module, we resolve its dependencies
            if (isModuleImport) { 
                // we are importing a module from another application
                resolveDependencies(item);
            } 
            // This will never run as it is blocked off in the UI
            else { // all object types are imported singularly
                if (window.apm.getNode(item.node.id, item.node.type) === null) {
                    treeDom.jstree().create_node(item.node.type + "s", Utility.getTreeNode(item.node), "last", function (new_node) {
                        treeDom.jstree().deselect_all();
                        treeDom.jstree().select_node(new_node);
                        //hide node
                        if (item.isHidden) $('#tl_container').jstree().hide_node(new_node);
                    });
                }
                window.apm.setNode(item.node.id, item.node);
                w2popup.close();
                if (this.props.event) {
                    window.apm.postEventToCurrentView({ target: "request-create-node-at-event", event: this.props.event, node: item.node });
                }
            }
            
            return item.node;
        }
    }

    /**
     * Allow for custom views that may exclude components from navigation
     * @returns {string} view type
     */
    getViewTypeForNavTabs(){
        if(this.coOrdinates && this.coOrdinates.insert){
            return 'object-only';
        } else if(this.coOrdinates && this.coOrdinates.replace){
            return 'modules-and-objects';
        } else {
            return 'default';
        }
    }
    
    /**
     * Render nav tabs based on vew type
     */
    getNavs(){
        const viewType = this.getViewTypeForNavTabs();

        if(viewType === 'object-only'){
            return (
                <ul className="nav nav-tabs">
                    <li className="nav-button">
                        <a data-toggle="tab" href="#new_object" className="new_object">{window.l.popup.object}</a>
                    </li>
                </ul>
            );
        } else if(viewType === 'modules-and-objects'){
            return (
                <ul className="nav nav-tabs">
                    <li className="nav-button">
                        <a data-toggle="tab" href="#new_module" className="new_module">{window.l.popup.module}</a>
                    </li>
                    
                    <li><div className="break" /></li>
                    
                    <li className="nav-button">
                        <a data-toggle="tab" href="#new_object" className="new_object">{window.l.popup.object}</a>
                    </li>
                </ul>
            );
        } else {
            return (
                <ul className="nav nav-tabs">       
                    <li className="nav-button" style={{ paddingLeft: 0 }}>
                        <a data-toggle="tab" href="#new_intent" className="new_intent">{window.l.popup.intent}</a>
                    </li>

                    <li><div className="break" /></li>
                    
                    <li className="nav-button">
                        <a data-toggle="tab" href="#new_module" className="new_module">{window.l.popup.module}</a>
                    </li>
                    
                    <li><div className="break" /></li>
                    
                    <li className="nav-button">
                        <a data-toggle="tab" href="#new_object" className="new_object">{window.l.popup.object}</a>
                    </li>
                    
                    {!Utility.getIsCommonModule(window.apm.serialized) && 
                        <li><div className="break" /></li>
                    }
                    {!Utility.getIsCommonModule(window.apm.serialized) && 
                        <li className="nav-button">
                            <a data-toggle="tab" href="#new_variable" className="new_variable">{window.l.popup.variable}</a>
                        </li>
                    }
                </ul>
            )
        }
    }

    /**
     * React render
     * @returns {XML}
     */
    render() {
        return (
            <div className="container-fluid element-dialog">
                <div className={"row"}>
                        
                        <div className="tab-title col-md-12">
                            {this.getNavs()}
                            <div className="dialog-search">
                                <div>
                                    <i className="fe icon-search input-prefix-icon" />
                                    <i className="fe icon-x input-suffix-icon clear-button"
                                        onClick={() => {
                                            this.getDomSelf().find("#dialog_q").val("");
                                            this.getDomSelf().find(".clear-button").hide();
                                            this.doFilter("");
                                        }} />
                                    <input type="search" id="dialog_q" className="form-component v-marginal search" placeholder={window.l.popup.search}
                                        onChange={() => {
                                            const val = this.getDomSelf().find("#dialog_q").val();
                                            if (val && val.length) {
                                                this.getDomSelf().find(".clear-button").show();
                                            } else {
                                                this.getDomSelf().find(".clear-button").hide();
                                            }
                                            this.doFilter(val);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-12">
                            <hr />
                        </div>
                        
                        <div className="tab-content col-md-12">
                            <div id="new_variable" className="tab-pane fade">
                                {this.renderElementItems("variable")}
                            </div>
                            <div id="new_object" className="tab-pane fade">
                                {this.renderElementItems("object")}
                            </div>
                            <div id="new_module" className="tab-pane fade">
                                {this.renderElementItems("module")}
                            </div>
                            <div id="new_intent" className="tab-pane fade">
                                {this.renderElementItems("intent")}
                            </div>
                        </div>
                                 
                </div>
            </div>
        );
    }
}
